/**
 * searchOsVerbs — the catalog's discovery surface (scale plane).
 *
 * Once the composer's inline inventory shrinks to the hot set, this lookup is
 * the ONLY way the model finds the rest of the registry — recall here is
 * product surface, and the full-corpus eval in os-verb-search.test.ts (every
 * row's examples must land that row in the top 3) is its bar. Pure and
 * in-memory: at thousands of rows a query costs single-digit milliseconds,
 * so the composer lane never pays a second network hop for discovery.
 *
 * Lexical by design (v1): tokenized field matching with weights, symmetric
 * plural-lite stemming, and a small stopword set applied to BOTH sides so the
 * mangling can never desync query from corpus. Embeddings join only if the
 * catalog outgrows keyword recall (spec §3.1) — the eval decides, not vibes.
 */
import { OS_VERB_REGISTRY, type OsVerbSpec } from './os-verbs.js';

export interface OsVerbSearchOpts {
  /** Registry to search (tests / future per-user catalogs); defaults to the live registry. */
  registry?: readonly OsVerbSpec[];
  /** Max hits returned (default 8). */
  limit?: number;
}

const DEFAULT_LIMIT = 8;

/** A single weak hit (score 1 = one description-only token) is noise, not a match. */
const MIN_SCORE = 2;

/** Applied to BOTH query and field text — symmetric, so stripped words can
 *  never exist on one side only. Deliberately small: over-stripping hurts
 *  short natural asks more than noise hurts ranking. */
const STOPWORDS = new Set([
  'a', 'an', 'the', 'to', 'of', 'in', 'on', 'at', 'is', 'it', 'my', 'i',
  'that', 'this', 'for', 'me', 'am', 'with', 'please',
]);

/** Lowercase, split on non-alphanumerics, drop stopwords/single chars, and
 *  strip a trailing 's' from longer tokens (plural-lite). The stem is applied
 *  to corpus and query alike, so 'tabs' finds 'tab' and vice versa. */
function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t))
    .map((t) => {
      if (t.length > 4 && t.endsWith('ed')) return t.slice(0, -2); // texted -> text
      if (t.length > 3 && t.endsWith('s')) return t.slice(0, -1); // tabs -> tab
      return t;
    });
}

/** Exact token match, or a prefix relation between two tokens long enough for
 *  prefixes to mean something ('direction' ↔ 'directions' post-stem class). */
function tokenMatch(qt: string, ft: string): boolean {
  if (qt === ft) return true;
  // Prefix relation only within a 2-char length delta: inflection pairs
  // (direction/directions, create/created) match; UNRELATED word swallowing
  // does not ('text' must never match 'textedit' — the wave-2 collision).
  if (qt.length >= 4 && ft.length >= 4 && Math.abs(qt.length - ft.length) <= 2) {
    return ft.startsWith(qt) || qt.startsWith(ft);
  }
  return false;
}

/** Field weights: naming an id/app/keyword is stronger intent than brushing a
 *  description. Each query token scores each field kind at most once. */
const FIELD_WEIGHTS = [
  { field: 'id' as const, weight: 4 },
  { field: 'title' as const, weight: 3 },
  { field: 'keywords' as const, weight: 3 },
  { field: 'app' as const, weight: 3 },
  { field: 'examples' as const, weight: 2 },
  { field: 'description' as const, weight: 1 },
];

type FieldTokens = Record<(typeof FIELD_WEIGHTS)[number]['field'], readonly string[]>;

/** Per-spec token index, built once per spec object (registry rows are module
 *  constants, so this amortizes to zero across queries). */
const specIndex = new WeakMap<OsVerbSpec, FieldTokens>();

function indexSpec(spec: OsVerbSpec): FieldTokens {
  const cached = specIndex.get(spec);
  if (cached) return cached;
  const built: FieldTokens = {
    id: tokenize(spec.id),
    title: tokenize(spec.title),
    keywords: tokenize((spec.keywords ?? []).join(' ')),
    app: spec.app ? tokenize(spec.app.name) : [],
    examples: tokenize((spec.examples ?? []).join(' ')),
    description: tokenize(spec.description),
  };
  specIndex.set(spec, built);
  return built;
}

export function searchOsVerbs(query: string, opts?: OsVerbSearchOpts): OsVerbSpec[] {
  const registry = opts?.registry ?? OS_VERB_REGISTRY;
  const limit = opts?.limit ?? DEFAULT_LIMIT;
  const qTokens = [...new Set(tokenize(query))];
  if (qTokens.length === 0 || limit <= 0) return [];

  const scored: { spec: OsVerbSpec; score: number }[] = [];
  for (const spec of registry) {
    const fields = indexSpec(spec);
    let score = 0;
    for (const qt of qTokens) {
      // Coverage dominates: one query token contributes its BEST field weight,
      // never a stack across fields — a row matching four of the user's words
      // once must beat a row matching one word four ways ('text mom' is
      // messages.send, not a textedit row whose id+title+keywords all say
      // 'text'). Found by the wave-2 recall eval.
      let best = 0;
      for (const { field, weight } of FIELD_WEIGHTS) {
        if (weight > best && fields[field].some((ft) => tokenMatch(qt, ft))) best = weight;
      }
      score += best;
    }
    if (score >= MIN_SCORE) scored.push({ spec, score });
  }

  scored.sort((a, b) => b.score - a.score || (a.spec.id < b.spec.id ? -1 : 1));
  return scored.slice(0, limit).map((s) => s.spec);
}
